import { Box, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { NavLink, useLocation } from "react-router-dom";
import { tokens } from "../../styles/Theme/Theme";
import { NAV_ITEMS, isHiddenOn, descriptionId } from "./navItems";

// Each label carries a subtitle saying what the page holds - the same line the
// desktop links carry as their accessible description. One list: navItems.js.
const itemStyles = {
  textDecoration: "none",
  color: tokens.colors.text.primary,
  padding: "10px 24px",
  minHeight: "60px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  borderRadius: tokens.borderRadius.md,
  transition: `color ${tokens.transitions.fast}, transform ${tokens.transitions.fast}`,
  "& .nav-label": {
    display: "inline-flex",
    alignItems: "center",
    gap: 1.25,
    fontFamily: tokens.typography.fontFamily.heading,
    fontSize: "24px",
    fontWeight: tokens.typography.fontWeight.medium,
    lineHeight: 1.2,
    position: "relative",
  },
  "& .nav-desc": {
    fontFamily: "Kalam, cursive",
    fontSize: "14px",
    lineHeight: 1.35,
    color: "rgba(255, 255, 255, 0.72)",
    mt: 0.25,
  },
  "&:hover .nav-label, &:focus .nav-label": {
    color: tokens.colors.brand.mint,
  },
  // Current page: a short brand underline under the label.
  "&[aria-current='page'] .nav-label::after": {
    content: '""',
    position: "absolute",
    left: 0,
    right: 0,
    bottom: "-4px",
    height: "2px",
    borderRadius: "2px",
    background: "linear-gradient(90deg, #FFD93B, #FFA63C, #FF5E62)",
  },
  "&:active": {
    transform: "scale(0.98)",
  },
  "&:focus-visible": {
    outline: `2px solid ${tokens.colors.brand.blue}`,
    outlineOffset: "4px",
  },
};

const NavbarMobile = ({ open, onClose }) => {
  const { pathname } = useLocation();
  const items = NAV_ITEMS.filter((item) => !isHiddenOn(item, pathname));

  return (
    <Box
      id="mobile-navigation"
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        visibility: open ? "visible" : "hidden",
        pointerEvents: open ? "auto" : "none",
        // Wait for the fade-out to finish before hiding, and show immediately.
        transition: open ? "visibility 0s" : "visibility 0s linear 400ms",
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Mobile navigation menu"
    >
      {/* Menu Panel. Opaque enough (95%) that a backdrop blur behind it would
          be invisible, so there is none - it only cost GPU work while open. */}
      <Box
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
        sx={{
          position: "absolute",
          inset: 0,
          background: "rgba(18, 18, 18, 0.95)",
          display: "flex",
          flexDirection: "column",
          transform: open ? "translateY(0)" : "translateY(-20px)",
          opacity: open ? 1 : 0,
          transition: `transform 0.4s ${tokens.motion.cssEase.out}, opacity ${tokens.transitions.normal}`,
          "@media (prefers-reduced-motion: reduce)": { transition: "none", transform: "none" },
        }}
      >
        {/* Close Button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            p: 2,
          }}
        >
          <IconButton
            onClick={onClose}
            aria-label="Close menu"
            sx={{
              color: tokens.colors.text.primary,
              border: `1px solid ${tokens.colors.background.glassBorder}`,
              borderRadius: tokens.borderRadius.md,
              padding: "12px",
              transition: `background-color ${tokens.transitions.fast}, transform ${tokens.transitions.fast}`,
              "&:hover": {
                background: tokens.colors.background.glass,
                transform: "rotate(90deg)",
              },
              "&:focus-visible": {
                outline: `2px solid ${tokens.colors.brand.blue}`,
                outlineOffset: "2px",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Navigation Items. The list scrolls when a short phone cannot fit it;
            the auto margins centre it when it can (justify-content: center
            would instead clip the top of an overflowing list). */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            px: 3,
            pb: 4,
          }}
        >
          <Box
            component="ul"
            sx={{
              listStyle: "none",
              m: 0,
              p: 0,
              my: "auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.25,
            }}
          >
            {items.map((item, index) => {
              const { Icon } = item;
              const delay = `${index * 0.05}s`;
              const descId = `${descriptionId(item.key)}-mobile`;
              return (
                <Box
                  component="li"
                  key={item.key}
                  sx={{
                    opacity: open ? 1 : 0,
                    "@media (prefers-reduced-motion: no-preference)": {
                      transform: open ? "translateY(0)" : "translateY(20px)",
                      transition: `opacity ${tokens.transitions.normal} ${delay}, transform 0.4s ${tokens.motion.cssEase.out} ${delay}`,
                    },
                  }}
                >
                  <Box
                    component={NavLink}
                    to={item.to}
                    end={item.to === "/"}
                    onClick={onClose}
                    // Named by its label, described by the line under it, so a
                    // screen reader announces "Work, link, Case studies, ...".
                    aria-label={item.label}
                    aria-describedby={descId}
                    sx={itemStyles}
                  >
                    <Box component="span" className="nav-label">
                      {Icon && <Icon sx={{ fontSize: 26 }} aria-hidden="true" />}
                      {item.label}
                    </Box>
                    <Box component="span" className="nav-desc" id={descId}>
                      {item.description}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default NavbarMobile;
